import { Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, TextField, Typography } from '@mui/material'
import AuthPageTestimonial from 'components/page-content/AuthPageTestimonial'
import AuthFlowPageWrapper from 'components/wrappers/AuthFlowPageWrapper'
import Head from 'next/head'
import Link from 'next/link'
import React from 'react'
import validator from 'validator'
import Immutable from 'immutable'
import styles from './authPages.module.scss'
import FormError from 'components/page-content/FormError'
import FormFeedbackWrapper from 'components/page-content/FormFeedbackWrapper'
import axios from 'axios'
import Context from 'utils/Context'
import { useRouter } from 'next/router'
import getConfig from 'next/config'

const { publicRuntimeConfig } = getConfig();

export default function CreateAccount() {

  const context = React.useContext(Context);
  const router = useRouter();

  const [fieldValues, setFieldValues] = React.useState(Immutable.fromJS({}));
  const [fieldErrors, setFieldErrors] = React.useState(Immutable.fromJS({}));
  const [formErrors, setFormErrors] = React.useState(Immutable.fromJS({}));
  const [fetching, setFetching] = React.useState(false);

  const onValueChange = (newValue, fieldKey) => {
    setFieldValues(fieldValues.set(fieldKey, newValue));
    doValidation(newValue, fieldKey);
  }

  const doValidation = (newValue, fieldKey) => {
    switch (fieldKey) {
      case 'email':
        if (validator.isEmpty(newValue)) {
          setFieldErrors(fieldErrors.set(fieldKey, 'Please enter your email address.'));
          return false;
        } else if (!validator.isEmail(newValue)) {
          setFieldErrors(fieldErrors.set(fieldKey, 'Please enter a valid email address.'));
          return false;
        } else {
          setFieldErrors(fieldErrors.delete(fieldKey));
          return true;
        }

      case 'password':
        if (validator.isEmpty(newValue)) {
          setFieldErrors(fieldErrors.set(fieldKey, 'Please enter your password.'));
          return false;
        } else {
          setFieldErrors(fieldErrors.delete(fieldKey));
          return true;
        }

      case 'confirmPassword':
        if (validator.isEmpty(newValue)) {
          setFieldErrors(fieldErrors.set(fieldKey, 'Please confirm your password.'));
          return false;
        } else if (fieldValues.get('password') !== newValue) {
          setFieldErrors(fieldErrors.set(fieldKey, 'Your chosen passwords must match.'));
          return false;
        } else {
          setFieldErrors(fieldErrors.delete(fieldKey));
          return true;
        }

      case 'fullName':
        if (validator.isEmpty(newValue)) {
          setFieldErrors(fieldErrors.set(fieldKey, 'Please provide your full name.'));
          return false;
        } else {
          setFieldErrors(fieldErrors.delete(fieldKey));
          return true;
        }

      case 'tosAgree':
        if (!newValue) {
          setFieldErrors(fieldErrors.set(fieldKey, 'You need to agree to our Terms of Service.'));
          return false;
        } else {
          setFieldErrors(fieldErrors.delete(fieldKey));
          return true;
        }

      default:
        return true;
    }
  }

  const checkRequiredFields = () => {
    const requiredFields = ['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'tosAgree'];
    let valid = true;
    requiredFields.forEach((field) => {
      if (valid) {
        valid = doValidation(fieldValues.get(field, ''), field);
      }
    })
    return valid;
  }

  const doSignUp = async (event) => {
    if (!checkRequiredFields()) return;
    setFetching(true);
    try {
      const response = await axios.post(`${publicRuntimeConfig.backendUrl}/api/auth/registration/`, {
        first_name: fieldValues.get('firstName'),
        last_name: fieldValues.get('lastName'),
        email: fieldValues.get('email'),
        password1: fieldValues.get('password'),
        password2: fieldValues.get('confirmPassword'),
      }, {
        withCredentials: true,
        headers: {
            "Content-type": "application/json",
        },
      });
      setFormErrors(Immutable.fromJS({}));
      router.push('/auth/create-account-success');
    } catch (error) {
      if (error.response?.status === 500) {
        setFormErrors(Immutable.fromJS({'generic_error': ['Uh-oh! We seem to have an error in our clouds :(']}));
      } else if (error.response?.data) {
        setFormErrors(Immutable.fromJS(error.response?.data));
      } else {
        setFormErrors(Immutable.fromJS({'generic_error': ['There was a problem with your request.']}));
      }
      setFetching(false);
    }
  }

  // handle enter on field
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      doSignIn();
    }
  }


  return (
    <div>
      <Head>
        <title>MyTags | Create Account</title>
      </Head>

      <AuthFlowPageWrapper
        asideContent={<AuthPageTestimonial />}
      >
        <div className={styles.authForm}>
          <Typography variant='h1'>
            Sign up
          </Typography>
          <Typography variant='subtitle1' sx={{ mb: 1 }}>
            Create your account in minutes.
          </Typography>
          <FormError errorsList={formErrors} />
          <FormFeedbackWrapper doFeedback={fetching}>
            <Typography variant='body1' sx={{ mb: 1 }}>
              It's FREE to try! No credit card required! If your needs outgrow our Free plan you can check out our paid plans (<strong><Link href='/pricing'>here</Link></strong>).
            </Typography>
            <TextField
              value={fieldValues.get('firstName', '')}
              onChange={(event) => onValueChange(event.target.value, 'firstName')}
              helperText={fieldErrors.get('firstName') || ''}
              error={fieldErrors.get('firstName') ? true : false}
              onKeyDown={handleKeyDown}
              variant='outlined'
              type='text'
              label='First name'
              placeholder='Your first name'
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              value={fieldValues.get('lastName', '')}
              onChange={(event) => onValueChange(event.target.value, 'lastName')}
              helperText={fieldErrors.get('lastName') || ''}
              error={fieldErrors.get('lastName') ? true : false}
              onKeyDown={handleKeyDown}
              variant='outlined'
              type='text'
              label='Last name'
              placeholder='Your last name'
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              value={fieldValues.get('email', '')}
              onChange={(event) => onValueChange(event.target.value, 'email')}
              helperText={fieldErrors.get('email') || ''}
              error={fieldErrors.get('email') ? true : false}
              onKeyDown={handleKeyDown}
              variant='outlined'
              type='email'
              label='Email'
              placeholder='Your email address'
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              value={fieldValues.get('password', '')}
              onChange={(event) => onValueChange(event.target.value, 'password')}
              helperText={fieldErrors.get('password') || ''}
              error={fieldErrors.get('password') ? true : false}
              onKeyDown={handleKeyDown}
              variant='outlined'
              type='password'
              label='Password'
              placeholder='Password'
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              value={fieldValues.get('confirmPassword', '')}
              onChange={(event) => onValueChange(event.target.value, 'confirmPassword')}
              helperText={fieldErrors.get('confirmPassword') || ''}
              error={fieldErrors.get('confirmPassword') ? true : false}
              onKeyDown={handleKeyDown}
              variant='outlined'
              type='password'
              label='Confirm Password'
              placeholder='Confirm password'
              fullWidth
              sx={{ mb: 2 }}
            />
            <FormControl
              required
              error={fieldErrors.get('tosAgree') ? true : false}
            >
              <FormGroup sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      onClick={(event) => onValueChange(event.target.checked, 'tosAgree')}
                    />
                  }
                  label={<span>I agree to <Link href='/terms-of-service'>Terms of Service</Link> and <Link href='/privacy-policy'>Privacy Policy</Link></span>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                    onClick={(event) => onValueChange(event.target.checked, 'marketingEmailAgree')}
                    />
                  }
                  label='I am cool with you emailing me'
                />
              </FormGroup>
              <FormHelperText sx={{ mb: 4, mt: -1 }}>{fieldErrors.get('tosAgree') || ''}</FormHelperText>
            </FormControl>
            <div className={styles.signInControls}>
              <Button
                variant='contained'
                size='large'
                disableElevation
                fullWidth
                disabled={fieldErrors.size ? true : false}
                onClick={doSignUp}
              >
                Sign up
              </Button>
            </div>
          </FormFeedbackWrapper>
          <span className={styles.verticalSeparator} />
          <Typography variant='h3'>
            Already have an account?
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Well there's sure no need for you to sign up again then!
          </Typography>
          <div className={styles.signInControls}>
            <Link href='/sign-in'>
              <Typography variant='subtitle1' fontWeight='bold' color='primary' sx={{ mb: 0, cursor: 'pointer' }}>
                <u>Sign in</u>
              </Typography>
            </Link>
          </div>
        </div>
      </AuthFlowPageWrapper>
    </div>
  )
}

export async function getStaticProps() {

  return {
    props: {},
    revalidate: 10,
  }
}
